import { useEffect, useRef } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { AlertTriangleIcon, ArrowRightIcon } from 'lucide-react';
import { btnGhost, btnPrimary } from '../ui/styles';
import { BUFFER_MINUTES, endTime } from '../../core/rules/agenda';
import { dayLong } from '../../core/format/dates';
import { ease } from '../../core/format/motion';
import { blockLabel, firstName } from './agendaRules';
import type { DragTarget } from './useTimelineDrag';
import type { AgendaAppointment } from '../../core/types/agenda';
type Props = {
  appointment: AgendaAppointment;
  target: DragTarget;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

/**
 * Na het loslaten: eerst bevestigen, want de klant krijgt bericht. Een botsing of een tijd
 * buiten de openingstijden mag (Sarah beslist), maar staat er dan duidelijk bij.
 */
export function MoveConfirm({
  appointment: a,
  target,
  busy,
  onConfirm,
  onCancel
}: Props) {
  const reduce = useReducedMotion();
  const confirmRef = useRef<HTMLButtonElement>(null);

  // Focus op "Verzetten": Enter bevestigt, Escape (capture, vóór het zijpaneel) annuleert.
  useEffect(() => confirmRef.current?.focus({
    preventScroll: true
  }), []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || busy) return;
      e.preventDefault();
      e.stopPropagation();
      onCancel();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [busy, onCancel]);
  const clash = target.conflicts[0];
  const clashText = clash ? clash.kind === 'appointment' ? `Botst met ${clash.item.customer.name} om ${clash.item.time} (inclusief ${BUFFER_MINUTES} min opruimen).` : `Valt in "${blockLabel(clash.item.reason, clash.item.label)}" om ${clash.item.time}.` : null;
  const sameDay = target.date === a.date;
  const span = {
    time: target.time,
    durationMinutes: a.durationMinutes
  };
  return (
    // Op de telefoon boven de tabbalk (64px + veilige rand), zodat "Annuleren" nooit eronder valt.
    <div className="pointer-events-none fixed inset-x-0 bottom-[calc(4.5rem+env(safe-area-inset-bottom))] z-bar flex justify-center px-3 lg:bottom-4" data-id="element-419" data-mp="3259wf6soxyvg">
      <motion.div role="dialog" aria-modal="false" aria-labelledby="move-confirm-title" initial={reduce ? false : {
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={reduce ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 8
      }} transition={{
        duration: 0.18,
        ease
      }} className="pointer-events-auto w-full max-w-lg rounded-lg border border-line bg-white p-4 shadow-canvas" data-id="element-420" data-mp="3by8oiynec7jt">
        <h2 id="move-confirm-title" className="text-[15px] font-medium text-ink" data-id="element-421" data-mp="2t5eln331s5vy">
          {firstName(a.customer.name)} verzetten?
        </h2>
        <p className="mt-1 flex flex-wrap items-center gap-x-2 text-[14px] text-ink" data-id="element-422" data-mp="8h0akpvshrud">
          <span className="tabular-nums text-muted line-through" data-id="element-423" data-mp="chsi9ep0jooj">
            {sameDay ? '' : `${dayLong(a.date)}, `}
            {a.time}
          </span>
          <ArrowRightIcon className="h-3.5 w-3.5 text-muted" strokeWidth={1.75} aria-hidden="true" data-id="element-424" data-mp="1wzbgqrig91eq" />
          <span className="tabular-nums" data-id="element-425" data-mp="1szb2p7z9is87">
            {sameDay ? '' : `${dayLong(target.date)}, `}
            {target.time}–{endTime(span)}
          </span>
        </p>
        {target.snappedToNow && <p className="mt-1 text-[13px] text-muted" data-id="element-426" data-mp="1j86mk4xup1bs">Het eerste kwartier na nu; eerder kan niet meer.</p>}
        {(clashText || target.outside) && <p className="mt-2 flex items-start gap-2 rounded-md bg-blush/60 px-3 py-2 text-[13px] text-ink" data-id="element-427" data-mp="19as7a8nnyil2">
            <AlertTriangleIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" strokeWidth={1.75} aria-hidden="true" data-id="element-428" data-mp="158f3vqwe2wrg" />
            <span data-id="element-429" data-mp="10alvcr7m5qo0">
              {clashText}
              {clashText && target.outside ? ' ' : ''}
              {target.outside && 'Valt (deels) buiten de openingstijden.'}
            </span>
          </p>}
        <p className="mt-2 text-[12px] text-muted" data-id="element-430" data-mp="2up6ev0k77g5t">{firstName(a.customer.name)} krijgt bericht met de nieuwe tijd.</p>
        <div className="mt-3 flex flex-wrap gap-2" data-id="element-431" data-mp="1rdqw18m9vdmu">
          <button ref={confirmRef} type="button" onClick={onConfirm} disabled={busy} aria-busy={busy} className={btnPrimary} data-id="element-432" data-mp="1wck9j11k74n0">
            {busy ? 'Verzetten…' : clash ? 'Toch verzetten' : 'Verzetten'}
          </button>
          <button type="button" onClick={onCancel} disabled={busy} className={btnGhost} data-id="element-433" data-mp="1na1bqvcmri7m">
            Annuleren
          </button>
        </div>
      </motion.div>
    </div>
  );
}